/**
 * 王族预设档案
 * 各时代各势力的初始王族成员完整档案
 * 
 * 结构：
 * - 君主（国君）
 * - 王后/夫人（君主正妻）
 * - 世子/太子（继承人）
 * - 公子/公主（其他子女）
 * - 公弟（君主弟弟）
 * - 重臣（重要朝臣）
 */

import type { CharacterIdentity } from '../types/characterArchive'

/**
 * 预设人物档案
 */
export interface PresetCharacter {
  id: string // 唯一标识，如 "shang_late_shang_ruler"
  name: string // 姓名（含姓氏）
  surname: string // 姓
  clanName?: string // 氏
  courtesyName?: string // 字
  givenName: string // 名
  gender: 'male' | 'female'
  birthYear: number // 相对于剧本开始年份的出生年（负数表示前）
  identity: CharacterIdentity
  title: string // 身份称号（如：商王、王后、太子）
  
  // 能力预设（可选，不设置则随机生成）
  abilities?: {
    intelligence?: number // 智略
    charisma?: number // 魅力
    strength?: number // 勇武
    diplomacy?: number // 外交
    administration?: number // 内政
    military?: number // 军事
  }
  
  // 性格预设（可选）
  personality?: {
    openness?: number // 开放性
    conscientiousness?: number // 尽责性
    extraversion?: number // 外向性
    agreeableness?: number // 宜人性
    neuroticism?: number // 神经质
  }
  
  // 流派
  school?: 'confucian' | 'legalist' | 'daoist' | 'mohist' | 'diplomat' | 'military' | 'yin_yang' | 'eclectic' | 'none'
  
  // 描述
  description: string
  
  // 历史评价
  historicalNote?: string
}

/**
 * 势力王族档案
 */
export interface FactionRoyalArchive {
  factionId: string
  scenarioId: string
  rulingFamily: {
    surname: string
    clanName?: string
    founder?: string
    description: string
  }
  members: PresetCharacter[]
}

// ============================================
// 商朝早期（约前1600-前1300年）
// 商汤开国，定都亳
// ============================================

export const SHANG_EARLY_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  // 商朝王族 - 子姓
  {
    factionId: 'shang',
    scenarioId: 'cheng-tang-revolution',
    rulingFamily: {
      surname: '子',
      clanName: '商',
      description: '商汤之后，成汤革命，建立商朝',
    },
    members: [
      {
        id: 'shang_early_shang_ruler',
        name: '子履',
        surname: '子',
        clanName: '商',
        givenName: '履',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '商王',
        abilities: {
          intelligence: 85,
          charisma: 90,
          strength: 75,
          diplomacy: 80,
          administration: 85,
          military: 80,
        },
        personality: {
          openness: 80,
          conscientiousness: 85,
          extraversion: 75,
          agreeableness: 70,
          neuroticism: 40,
        },
        school: 'eclectic',
        description: '商朝开国君主，推翻夏桀，建立商朝',
        historicalNote: '商汤，名履，史称成汤。任用伊尹，推翻夏朝，建立商朝，是商朝最伟大的君主之一。',
      },
      {
        id: 'shang_early_shang_queen',
        name: '子氏',
        surname: '子',
        givenName: '氏',
        gender: 'female',
        birthYear: -42,
        identity: 'royal',
        title: '王后',
        description: '商汤正妻，贤内助',
      },
      {
        id: 'shang_early_shang_heir',
        name: '子胜',
        surname: '子',
        clanName: '商',
        givenName: '胜',
        gender: 'male',
        birthYear: -25,
        identity: 'royal',
        title: '太子',
        description: '商汤太子，未来商朝继承人',
      },
      {
        id: 'shang_early_shang_prince1',
        name: '子雍',
        surname: '子',
        clanName: '商',
        givenName: '雍',
        gender: 'male',
        birthYear: -22,
        identity: 'royal',
        title: '公子',
        description: '商汤之子',
      },
      {
        id: 'shang_early_shang_princess1',
        name: '子嬕',
        surname: '子',
        givenName: '嬕',
        gender: 'female',
        birthYear: -20,
        identity: 'royal',
        title: '公主',
        description: '商汤之女',
      },
    ],
  },
  
  // 周朝王族 - 姬姓
  {
    factionId: 'zhou',
    scenarioId: 'cheng-tang-revolution',
    rulingFamily: {
      surname: '姬',
      clanName: '周',
      description: '后稷之后，周人早期部落',
    },
    members: [
      {
        id: 'shang_early_zhou_ruler',
        name: '姬亶',
        surname: '姬',
        clanName: '周',
        courtesyName: '古公',
        givenName: '亶',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '周君',
        abilities: {
          intelligence: 80,
          charisma: 85,
          strength: 70,
          diplomacy: 75,
          administration: 80,
          military: 70,
        },
        personality: {
          openness: 70,
          conscientiousness: 80,
          extraversion: 65,
          agreeableness: 75,
          neuroticism: 45,
        },
        school: 'eclectic',
        description: '周文王祖父，周人早期首领',
        historicalNote: '古公亶父，周文王的祖父，带领周人从豳迁到岐山之下的周原，奠定了周朝的基础。',
      },
      {
        id: 'shang_early_zhou_heir',
        name: '姬历',
        surname: '姬',
        clanName: '周',
        courtesyName: '王季',
        givenName: '历',
        gender: 'male',
        birthYear: -30,
        identity: 'royal',
        title: '世子',
        description: '古公亶父之子，周文王之父',
      },
      {
        id: 'shang_early_zhou_prince1',
        name: '姬昌',
        surname: '姬',
        clanName: '周',
        givenName: '昌',
        gender: 'male',
        birthYear: -10,
        identity: 'royal',
        title: '公子',
        description: '王季之子，未来的周文王',
      },
    ],
  },
  
  // 鬼方 - 隗姓
  {
    factionId: 'guifang',
    scenarioId: 'cheng-tang-revolution',
    rulingFamily: {
      surname: '隗',
      description: '北方游牧民族，隗姓部落',
    },
    members: [
      {
        id: 'shang_early_guifang_ruler',
        name: '隗满',
        surname: '隗',
        givenName: '满',
        gender: 'male',
        birthYear: -40,
        identity: 'ruler',
        title: '鬼方伯',
        abilities: {
          intelligence: 65,
          charisma: 70,
          strength: 85,
          diplomacy: 60,
          administration: 50,
          military: 80,
        },
        school: 'military',
        description: '鬼方部落首领，北方游牧民族领袖',
      },
      {
        id: 'shang_early_guifang_prince1',
        name: '隗突',
        surname: '隗',
        givenName: '突',
        gender: 'male',
        birthYear: -20,
        identity: 'royal',
        title: '王子',
        description: '鬼方伯之子',
      },
    ],
  },
  
  // 羌方 - 姜姓
  {
    factionId: 'qiangfang',
    scenarioId: 'cheng-tang-revolution',
    rulingFamily: {
      surname: '姜',
      description: '西方羌族部落，姜姓',
    },
    members: [
      {
        id: 'shang_early_qiangfang_ruler',
        name: '姜戎',
        surname: '姜',
        givenName: '戎',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '羌方伯',
        abilities: {
          intelligence: 60,
          charisma: 65,
          strength: 80,
          diplomacy: 55,
          administration: 45,
          military: 75,
        },
        school: 'military',
        description: '羌方部落首领，西方部落联盟领袖',
      },
    ],
  },
  
  // 人方 - 偃姓
  {
    factionId: 'renfang',
    scenarioId: 'cheng-tang-revolution',
    rulingFamily: {
      surname: '偃',
      description: '东夷部落，偃姓，少昊之后',
    },
    members: [
      {
        id: 'shang_early_renfang_ruler',
        name: '偃羿',
        surname: '偃',
        givenName: '羿',
        gender: 'male',
        birthYear: -40,
        identity: 'ruler',
        title: '人方伯',
        abilities: {
          intelligence: 70,
          charisma: 75,
          strength: 85,
          diplomacy: 65,
          administration: 55,
          military: 80,
        },
        school: 'military',
        description: '人方部落首领，东夷部落联盟领袖',
      },
    ],
  },
]

// ============================================
// 商朝中期（约前1300-前1100年）
// 盘庚迁殷，武丁中兴
// ============================================

export const SHANG_MIDDLE_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  // 商朝王族 - 子姓
  {
    factionId: 'shang',
    scenarioId: 'pan-geng-move',
    rulingFamily: {
      surname: '子',
      clanName: '商',
      description: '盘庚之后，迁都于殷',
    },
    members: [
      {
        id: 'shang_middle_shang_ruler',
        name: '子旬',
        surname: '子',
        clanName: '商',
        givenName: '旬',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '商王',
        abilities: {
          intelligence: 80,
          charisma: 75,
          strength: 70,
          diplomacy: 70,
          administration: 85,
          military: 65,
        },
        personality: {
          openness: 60,
          conscientiousness: 85,
          extraversion: 50,
          agreeableness: 60,
          neuroticism: 50,
        },
        school: 'eclectic',
        description: '盘庚，名旬，迁都于殷，商朝中兴起',
        historicalNote: '盘庚，名旬，商朝第十九位君主。迁都于殷（今河南安阳），使商朝中兴，史称"盘庚迁殷"。',
      },
      {
        id: 'shang_middle_shang_queen',
        name: '子氏',
        surname: '子',
        givenName: '氏',
        gender: 'female',
        birthYear: -40,
        identity: 'royal',
        title: '王后',
        description: '盘庚正妻',
      },
      {
        id: 'shang_middle_shang_heir',
        name: '子颂',
        surname: '子',
        clanName: '商',
        givenName: '颂',
        gender: 'male',
        birthYear: -25,
        identity: 'royal',
        title: '太子',
        description: '盘庚太子',
      },
    ],
  },
  
  // 周朝王族 - 姬姓
  {
    factionId: 'zhou',
    scenarioId: 'pan-geng-move',
    rulingFamily: {
      surname: '姬',
      clanName: '周',
      description: '姬昌在位，周人崛起',
    },
    members: [
      {
        id: 'shang_middle_zhou_ruler',
        name: '姬昌',
        surname: '姬',
        clanName: '周',
        courtesyName: '伯',
        givenName: '昌',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '周君',
        abilities: {
          intelligence: 95,
          charisma: 90,
          strength: 70,
          diplomacy: 85,
          administration: 90,
          military: 80,
        },
        personality: {
          openness: 85,
          conscientiousness: 90,
          extraversion: 75,
          agreeableness: 85,
          neuroticism: 35,
        },
        school: 'confucian',
        description: '周文王，周朝奠基人，被商纣王囚禁于羑里',
        historicalNote: '周文王，名昌，周朝奠基人。被商纣王囚禁于羑里，演《周易》，后获释，继续积蓄力量。',
      },
      {
        id: 'shang_middle_zhou_queen',
        name: '姒氏',
        surname: '姒',
        givenName: '氏',
        gender: 'female',
        birthYear: -45,
        identity: 'royal',
        title: '夫人',
        description: '周文王正妻，太姒',
      },
      {
        id: 'shang_middle_zhou_heir',
        name: '姬发',
        surname: '姬',
        clanName: '周',
        givenName: '发',
        gender: 'male',
        birthYear: -30,
        identity: 'royal',
        title: '世子',
        description: '周文王之子，未来的周武王',
      },
      {
        id: 'shang_middle_zhou_prince1',
        name: '姬旦',
        surname: '姬',
        clanName: '周',
        givenName: '旦',
        gender: 'male',
        birthYear: -28,
        identity: 'royal',
        title: '公子',
        description: '周公旦，周文王之子，未来的周公',
      },
    ],
  },
  
  // 鬼方 - 隗姓
  {
    factionId: 'guifang',
    scenarioId: 'pan-geng-move',
    rulingFamily: {
      surname: '隗',
      description: '北方鬼方部落',
    },
    members: [
      {
        id: 'shang_middle_guifang_ruler',
        name: '隗满',
        surname: '隗',
        givenName: '满',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '鬼方伯',
        abilities: {
          intelligence: 65,
          charisma: 70,
          strength: 85,
          diplomacy: 60,
          administration: 50,
          military: 80,
        },
        school: 'military',
        description: '鬼方部落首领',
      },
    ],
  },
  
  // 羌方 - 姜姓
  {
    factionId: 'qiangfang',
    scenarioId: 'pan-geng-move',
    rulingFamily: {
      surname: '姜',
      description: '西方羌族',
    },
    members: [
      {
        id: 'shang_middle_qiangfang_ruler',
        name: '姜伯',
        surname: '姜',
        givenName: '伯',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '羌方伯',
        abilities: {
          intelligence: 60,
          charisma: 65,
          strength: 80,
          diplomacy: 55,
          administration: 45,
          military: 75,
        },
        school: 'military',
        description: '羌方部落首领',
      },
    ],
  },
  
  // 人方 - 偃姓
  {
    factionId: 'renfang',
    scenarioId: 'pan-geng-move',
    rulingFamily: {
      surname: '偃',
      description: '东夷人方',
    },
    members: [
      {
        id: 'shang_middle_renfang_ruler',
        name: '偃和',
        surname: '偃',
        givenName: '和',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '人方伯',
        abilities: {
          intelligence: 70,
          charisma: 75,
          strength: 85,
          diplomacy: 65,
          administration: 55,
          military: 80,
        },
        school: 'military',
        description: '人方部落首领',
      },
    ],
  },
]

// ============================================
// 商朝晚期（约前1100-前1046年）
// 帝辛（纣王）时期，周文王、武王崛起
// ============================================

export const SHANG_LATE_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  // 商朝王族 - 子姓
  {
    factionId: 'shang',
    scenarioId: 'wu-ding-prosperity',
    rulingFamily: {
      surname: '子',
      clanName: '商',
      description: '帝辛之后，商朝末世',
    },
    members: [
      {
        id: 'shang_late_shang_ruler',
        name: '子受',
        surname: '子',
        clanName: '商',
        courtesyName: '纣',
        givenName: '受',
        gender: 'male',
        birthYear: -35,
        identity: 'ruler',
        title: '商王',
        abilities: {
          intelligence: 75,
          charisma: 70,
          strength: 90,
          diplomacy: 50,
          administration: 40,
          military: 75,
        },
        personality: {
          openness: 60,
          conscientiousness: 30,
          extraversion: 80,
          agreeableness: 30,
          neuroticism: 70,
        },
        school: 'eclectic',
        description: '帝辛，史称纣王，商朝末代君主',
        historicalNote: '帝辛，名受（或受德），商朝末代君主。史书记载其沉湎酒色、重刑厚敛，牧野之战败于周武王，自焚于鹿台。',
      },
      {
        id: 'shang_late_shang_queen',
        name: '妲己',
        surname: '己',
        clanName: '苏',
        givenName: '妲',
        gender: 'female',
        birthYear: -28,
        identity: 'royal',
        title: '王后',
        abilities: {
          charisma: 95,
          intelligence: 70,
        },
        description: '妲己，有苏氏之女，商纣王宠妃',
        historicalNote: '妲己，己姓，有苏氏之女。史书记载其美貌绝伦，纣王对其言听计从，是导致商朝灭亡的重要因素之一。',
      },
      {
        id: 'shang_late_shang_heir',
        name: '子禄',
        surname: '子',
        clanName: '商',
        givenName: '禄',
        gender: 'male',
        birthYear: -15,
        identity: 'royal',
        title: '太子',
        description: '商纣王太子',
      },
      {
        id: 'shang_late_shang_prince1',
        name: '子启',
        surname: '子',
        clanName: '商',
        givenName: '启',
        gender: 'male',
        birthYear: -38,
        identity: 'royal',
        title: '微子',
        description: '微子启，纣王庶兄，后归周',
        historicalNote: '微子启，子姓，名启，商纣王庶兄。因不满纣王统治，投奔周朝，后被周武王封于宋，建立宋国。',
      },
      {
        id: 'shang_late_shang_prince2',
        name: '子衍',
        surname: '子',
        clanName: '商',
        givenName: '衍',
        gender: 'male',
        birthYear: -32,
        identity: 'royal',
        title: '公子',
        description: '商纣王之子',
      },
    ],
  },
  
  // 周朝王族 - 姬姓
  {
    factionId: 'zhou',
    scenarioId: 'wu-ding-prosperity',
    rulingFamily: {
      surname: '姬',
      clanName: '周',
      description: '周文王、武王，准备伐商',
    },
    members: [
      {
        id: 'shang_late_zhou_ruler',
        name: '姬昌',
        surname: '姬',
        clanName: '周',
        courtesyName: '伯',
        givenName: '昌',
        gender: 'male',
        birthYear: -65,
        identity: 'ruler',
        title: '周文王',
        abilities: {
          intelligence: 95,
          charisma: 90,
          strength: 65,
          diplomacy: 90,
          administration: 95,
          military: 85,
        },
        personality: {
          openness: 90,
          conscientiousness: 95,
          extraversion: 75,
          agreeableness: 90,
          neuroticism: 30,
        },
        school: 'confucian',
        description: '周文王，周朝奠基人，礼贤下士',
        historicalNote: '周文王，名昌，在位50年。被商纣王囚禁于羑里七年，演《周易》。礼贤下士，姜子牙辅佐，为灭商奠定基础。',
      },
      {
        id: 'shang_late_zhou_queen',
        name: '姒氏',
        surname: '姒',
        givenName: '氏',
        gender: 'female',
        birthYear: -60,
        identity: 'royal',
        title: '太姒',
        description: '周文王正妻，贤德之妇',
        historicalNote: '太姒，姒姓，周文王正妻。传说她生有十子，包括周武王和周公旦，被后世尊为贤德之妇的典范。',
      },
      {
        id: 'shang_late_zhou_heir',
        name: '姬发',
        surname: '姬',
        clanName: '周',
        givenName: '发',
        gender: 'male',
        birthYear: -40,
        identity: 'royal',
        title: '周武王',
        abilities: {
          intelligence: 85,
          charisma: 90,
          strength: 85,
          diplomacy: 80,
          administration: 85,
          military: 95,
        },
        personality: {
          openness: 75,
          conscientiousness: 90,
          extraversion: 80,
          agreeableness: 80,
          neuroticism: 40,
        },
        school: 'military',
        description: '周武王，伐商灭纣，建立周朝',
        historicalNote: '周武王，名发，周文王次子。继承父志，在姜子牙辅佐下，牧野之战大败商军，灭商建立周朝。',
      },
      {
        id: 'shang_late_zhou_prince1',
        name: '姬旦',
        surname: '姬',
        clanName: '周',
        givenName: '旦',
        gender: 'male',
        birthYear: -38,
        identity: 'royal',
        title: '周公',
        abilities: {
          intelligence: 95,
          charisma: 85,
          strength: 60,
          diplomacy: 90,
          administration: 95,
          military: 70,
        },
        school: 'confucian',
        description: '周公旦，制礼作乐，辅佐成王',
        historicalNote: '周公旦，姬姓，名旦，周文王第四子。制礼作乐，建立周朝典章制度，辅佐周成王，被尊为儒家先驱。',
      },
      {
        id: 'shang_late_zhou_prince2',
        name: '姬奭',
        surname: '姬',
        clanName: '周',
        givenName: '奭',
        gender: 'male',
        birthYear: -42,
        identity: 'royal',
        title: '召公',
        description: '召公奭，周文王之子，与周公共同辅政',
      },
    ],
  },
  
  // 鬼方 - 隗姓
  {
    factionId: 'guifang',
    scenarioId: 'wu-ding-prosperity',
    rulingFamily: {
      surname: '隗',
      description: '北方鬼方，商朝边患',
    },
    members: [
      {
        id: 'shang_late_guifang_ruler',
        name: '隗满',
        surname: '隗',
        givenName: '满',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '鬼方伯',
        abilities: {
          intelligence: 65,
          charisma: 70,
          strength: 85,
          diplomacy: 60,
          administration: 50,
          military: 80,
        },
        school: 'military',
        description: '鬼方部落首领，北方游牧民族',
      },
    ],
  },
  
  // 羌方 - 姜姓
  {
    factionId: 'qiangfang',
    scenarioId: 'wu-ding-prosperity',
    rulingFamily: {
      surname: '姜',
      description: '西方羌族',
    },
    members: [
      {
        id: 'shang_late_qiangfang_ruler',
        name: '姜伯',
        surname: '姜',
        givenName: '伯',
        gender: 'male',
        birthYear: -55,
        identity: 'ruler',
        title: '羌方伯',
        abilities: {
          intelligence: 60,
          charisma: 65,
          strength: 80,
          diplomacy: 55,
          administration: 45,
          military: 75,
        },
        school: 'military',
        description: '羌方部落首领',
      },
    ],
  },
  
  // 人方 - 偃姓
  {
    factionId: 'renfang',
    scenarioId: 'wu-ding-prosperity',
    rulingFamily: {
      surname: '偃',
      description: '东夷人方，帝辛征讨对象',
    },
    members: [
      {
        id: 'shang_late_renfang_ruler',
        name: '偃和',
        surname: '偃',
        givenName: '和',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '人方伯',
        abilities: {
          intelligence: 70,
          charisma: 75,
          strength: 85,
          diplomacy: 65,
          administration: 55,
          military: 80,
        },
        school: 'military',
        description: '人方部落首领，东夷部落联盟领袖',
      },
    ],
  },
]

// ============================================
// 春秋时代 - 早期（前770-前680年）
// ============================================

export const SPRING_AUTUMN_EARLY_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  // 周朝 - 姬姓
  {
    factionId: 'zhou',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '姬',
      clanName: '周',
      description: '周平王东迁，东周开始',
    },
    members: [
      {
        id: 'sa_early_zhou_ruler',
        name: '姬宜臼',
        surname: '姬',
        clanName: '周',
        givenName: '宜臼',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '周平王',
        abilities: {
          intelligence: 70,
          charisma: 65,
          strength: 50,
          diplomacy: 75,
          administration: 60,
          military: 40,
        },
        school: 'confucian',
        description: '周平王，东周开国君主，迁都洛邑',
        historicalNote: '周平王，名宜臼，周幽王之子。犬戎攻破镐京后，迁都洛邑，东周开始，周王室衰微。',
      },
    ],
  },
  
  // 齐国 - 姜姓吕氏
  {
    factionId: 'qi',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '姜',
      clanName: '吕',
      description: '太公望之后，春秋大国',
    },
    members: [
      {
        id: 'sa_early_qi_ruler',
        name: '吕购',
        surname: '姜',
        clanName: '吕',
        givenName: '购',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '齐庄公',
        abilities: {
          intelligence: 70,
          charisma: 75,
          strength: 65,
          diplomacy: 70,
          administration: 75,
          military: 70,
        },
        school: 'eclectic',
        description: '齐庄公，齐国国君',
      },
    ],
  },
  
  // 晋国 - 姬姓
  {
    factionId: 'jin',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '姬',
      clanName: '晋',
      description: '周王室分支，春秋霸主',
    },
    members: [
      {
        id: 'sa_early_jin_ruler',
        name: '姬仇',
        surname: '姬',
        clanName: '晋',
        givenName: '仇',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '晋文侯',
        abilities: {
          intelligence: 75,
          charisma: 80,
          strength: 70,
          diplomacy: 85,
          administration: 80,
          military: 80,
        },
        school: 'military',
        description: '晋文侯，辅佐周平王东迁',
        historicalNote: '晋文侯，名仇，晋国第十一任国君。辅佐周平王东迁，平定周王室内乱，为晋国崛起奠定基础。',
      },
    ],
  },
  
  // 鲁国 - 姬姓
  {
    factionId: 'lu',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '姬',
      clanName: '鲁',
      description: '周公之后，礼仪之邦',
    },
    members: [
      {
        id: 'sa_early_lu_ruler',
        name: '姬弗湟',
        surname: '姬',
        clanName: '鲁',
        givenName: '弗湟',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '鲁惠公',
        description: '鲁惠公，鲁国国君',
      },
    ],
  },
  
  // 宋国 - 子姓
  {
    factionId: 'song',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '子',
      clanName: '宋',
      description: '商朝后裔，子姓',
    },
    members: [
      {
        id: 'sa_early_song_ruler',
        name: '子司空',
        surname: '子',
        clanName: '宋',
        givenName: '司空',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '宋武公',
        description: '宋武公，宋国国君',
      },
    ],
  },
  
  // 秦国 - 嬴姓
  {
    factionId: 'qin',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '嬴',
      clanName: '秦',
      description: '嬴姓，西陲大夫',
    },
    members: [
      {
        id: 'sa_early_qin_ruler',
        name: '嬴开',
        surname: '嬴',
        clanName: '秦',
        givenName: '开',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '秦襄公',
        abilities: {
          intelligence: 75,
          charisma: 75,
          strength: 80,
          diplomacy: 70,
          administration: 75,
          military: 85,
        },
        school: 'military',
        description: '秦襄公，护送平王东迁，始封诸侯',
        historicalNote: '秦襄公，名开，秦国正式被封为诸侯的第一位国君。因护送周平王东迁有功，被封为诸侯，赐给岐山以西之地。',
      },
    ],
  },
  
  // 楚国 - 芈姓熊氏
  {
    factionId: 'chu',
    scenarioId: 'spring_autumn_early',
    rulingFamily: {
      surname: '芈',
      clanName: '熊',
      description: '芈姓熊氏，南方大国',
    },
    members: [
      {
        id: 'sa_early_chu_ruler',
        name: '熊通',
        surname: '芈',
        clanName: '熊',
        givenName: '通',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '楚武王',
        abilities: {
          intelligence: 75,
          charisma: 80,
          strength: 85,
          diplomacy: 65,
          administration: 70,
          military: 90,
        },
        school: 'military',
        description: '楚武王，自称武王，楚国称王第一人',
        historicalNote: '楚武王，名通，楚国第十七任国君。不服周朝爵位，自称武王，是楚国称王的第一人，标志着楚国崛起。',
      },
    ],
  },
]

// ============================================
// 春秋时代 - 中期（前680-前550年）
// 齐桓公、晋文公称霸，春秋五霸时代
// ============================================

export const SPRING_AUTUMN_MIDDLE_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  // 齐国 - 姜姓吕氏
  {
    factionId: 'qi',
    scenarioId: 'spring_autumn_middle',
    rulingFamily: {
      surname: '姜',
      clanName: '吕',
      description: '齐桓公，春秋五霸之首',
    },
    members: [
      {
        id: 'sa_middle_qi_ruler',
        name: '吕小白',
        surname: '姜',
        clanName: '吕',
        givenName: '小白',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '齐桓公',
        abilities: {
          intelligence: 80,
          charisma: 90,
          strength: 75,
          diplomacy: 85,
          administration: 85,
          military: 85,
        },
        personality: {
          openness: 75,
          conscientiousness: 70,
          extraversion: 85,
          agreeableness: 70,
          neuroticism: 60,
        },
        school: 'eclectic',
        description: '齐桓公，春秋五霸之首，九合诸侯',
        historicalNote: '齐桓公，名小白，春秋五霸之首。任用管仲为相，"尊王攘夷"，九合诸侯，一匡天下，使齐国成为春秋首霸。',
      },
      {
        id: 'sa_middle_qi_chancellor',
        name: '管夷吾',
        surname: '管',
        givenName: '夷吾',
        gender: 'male',
        birthYear: -50,
        identity: 'official',
        title: '相国',
        abilities: {
          intelligence: 95,
          charisma: 80,
          diplomacy: 90,
          administration: 95,
          military: 75,
        },
        school: 'legalist',
        description: '管仲，法家先驱，齐国名相',
        historicalNote: '管仲，名夷吾，字仲，春秋时期著名政治家、军事家。辅佐齐桓公成为春秋首霸，被尊为"法家先驱"。',
      },
    ],
  },
  
  // 晋国 - 姬姓
  {
    factionId: 'jin',
    scenarioId: 'spring_autumn_middle',
    rulingFamily: {
      surname: '姬',
      clanName: '晋',
      description: '晋文公，春秋五霸之一',
    },
    members: [
      {
        id: 'sa_middle_jin_ruler',
        name: '姬重耳',
        surname: '姬',
        clanName: '晋',
        givenName: '重耳',
        gender: 'male',
        birthYear: -60,
        identity: 'ruler',
        title: '晋文公',
        abilities: {
          intelligence: 85,
          charisma: 90,
          strength: 70,
          diplomacy: 90,
          administration: 85,
          military: 90,
        },
        personality: {
          openness: 80,
          conscientiousness: 85,
          extraversion: 75,
          agreeableness: 80,
          neuroticism: 40,
        },
        school: 'confucian',
        description: '晋文公，流亡十九年，终成霸业',
        historicalNote: '晋文公，名重耳，春秋五霸之一。流亡十九年，62岁即位，城濮之战大败楚国，成为春秋霸主。',
      },
    ],
  },
  
  // 宋国 - 子姓
  {
    factionId: 'song',
    scenarioId: 'spring_autumn_middle',
    rulingFamily: {
      surname: '子',
      clanName: '宋',
      description: '宋襄公，春秋五霸之一',
    },
    members: [
      {
        id: 'sa_middle_song_ruler',
        name: '子兹甫',
        surname: '子',
        clanName: '宋',
        givenName: '兹甫',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '宋襄公',
        abilities: {
          intelligence: 60,
          charisma: 70,
          strength: 60,
          diplomacy: 65,
          administration: 60,
          military: 50,
        },
        personality: {
          openness: 50,
          conscientiousness: 80,
          extraversion: 60,
          agreeableness: 75,
          neuroticism: 50,
        },
        school: 'confucian',
        description: '宋襄公，以仁义著称，泓水之战败于楚',
        historicalNote: '宋襄公，名兹甫，春秋五霸之一（有争议）。以仁义著称，泓水之战中坚持"不鼓不成列"，被楚军击败。',
      },
    ],
  },
  
  // 秦国 - 嬴姓
  {
    factionId: 'qin',
    scenarioId: 'spring_autumn_middle',
    rulingFamily: {
      surname: '嬴',
      clanName: '秦',
      description: '秦穆公，春秋五霸之一',
    },
    members: [
      {
        id: 'sa_middle_qin_ruler',
        name: '嬴任好',
        surname: '嬴',
        clanName: '秦',
        givenName: '任好',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '秦穆公',
        abilities: {
          intelligence: 80,
          charisma: 85,
          strength: 80,
          diplomacy: 80,
          administration: 85,
          military: 85,
        },
        personality: {
          openness: 75,
          conscientiousness: 80,
          extraversion: 70,
          agreeableness: 75,
          neuroticism: 45,
        },
        school: 'eclectic',
        description: '秦穆公，称霸西戎，春秋五霸之一',
        historicalNote: '秦穆公，名任好，春秋五霸之一。任用百里奚、蹇叔等贤臣，称霸西戎，为秦国崛起奠定基础。',
      },
    ],
  },
  
  // 楚国 - 芈姓熊氏
  {
    factionId: 'chu',
    scenarioId: 'spring_autumn_middle',
    rulingFamily: {
      surname: '芈',
      clanName: '熊',
      description: '楚成王，楚国强盛时期',
    },
    members: [
      {
        id: 'sa_middle_chu_ruler',
        name: '熊頵',
        surname: '芈',
        clanName: '熊',
        givenName: '頵',
        gender: 'male',
        birthYear: -55,
        identity: 'ruler',
        title: '楚成王',
        abilities: {
          intelligence: 75,
          charisma: 80,
          strength: 80,
          diplomacy: 70,
          administration: 75,
          military: 85,
        },
        school: 'military',
        description: '楚成王，楚国强盛，城濮之战败于晋',
      },
    ],
  },
]

// ============================================
// 春秋时代 - 晚期（前550-前476年）
// 吴越争霸，春秋末期
// ============================================

export const SPRING_AUTUMN_LATE_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  // 齐国 - 姜姓吕氏
  {
    factionId: 'qi',
    scenarioId: 'spring_autumn_late',
    rulingFamily: {
      surname: '姜',
      clanName: '吕',
      description: '齐景公，春秋晚期名君',
    },
    members: [
      {
        id: 'sa_late_qi_ruler',
        name: '吕杵臼',
        surname: '姜',
        clanName: '吕',
        givenName: '杵臼',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '齐景公',
        abilities: {
          intelligence: 75,
          charisma: 75,
          strength: 60,
          diplomacy: 70,
          administration: 75,
          military: 65,
        },
        school: 'confucian',
        description: '齐景公，在位58年，齐国稳定时期',
        historicalNote: '齐景公，名杵臼，在位58年。晏婴为相，齐国保持强盛，但未能阻止陈氏（田氏）势力膨胀。',
      },
      {
        id: 'sa_late_qi_chancellor',
        name: '晏婴',
        surname: '晏',
        givenName: '婴',
        gender: 'male',
        birthYear: -60,
        identity: 'official',
        title: '相国',
        abilities: {
          intelligence: 90,
          charisma: 85,
          diplomacy: 90,
          administration: 90,
          military: 60,
        },
        school: 'confucian',
        description: '晏子，齐国名相，以节俭和机智著称',
        historicalNote: '晏婴，字仲，谥号"平"，史称晏子。齐国著名政治家、思想家、外交家，以节俭力行和机智善辩著称。',
      },
    ],
  },
  
  // 吴国 - 姬姓
  {
    factionId: 'wu',
    scenarioId: 'spring_autumn_late',
    rulingFamily: {
      surname: '姬',
      clanName: '吴',
      description: '吴王阖闾，称霸东南',
    },
    members: [
      {
        id: 'sa_late_wu_ruler',
        name: '姬光',
        surname: '姬',
        clanName: '吴',
        givenName: '光',
        gender: 'male',
        birthYear: -45,
        identity: 'ruler',
        title: '吴王阖闾',
        abilities: {
          intelligence: 80,
          charisma: 80,
          strength: 85,
          diplomacy: 75,
          administration: 80,
          military: 90,
        },
        personality: {
          openness: 70,
          conscientiousness: 75,
          extraversion: 75,
          agreeableness: 60,
          neuroticism: 55,
        },
        school: 'military',
        description: '吴王阖闾，任用伍子胥、孙武，称霸东南',
        historicalNote: '吴王阖闾，名光。派专诸刺杀吴王僚即位，任用伍子胥、孙武，攻破楚国郢都，称霸东南。',
      },
      {
        id: 'sa_late_wu_general',
        name: '孙武',
        surname: '孙',
        givenName: '武',
        gender: 'male',
        birthYear: -55,
        identity: 'general',
        title: '大将军',
        abilities: {
          intelligence: 95,
          charisma: 70,
          strength: 60,
          diplomacy: 60,
          administration: 75,
          military: 100,
        },
        school: 'military',
        description: '孙子，兵圣，著《孙子兵法》',
        historicalNote: '孙武，字长卿，春秋末期著名军事家、政治家，被尊为"兵圣"。著《孙子兵法》，是中国最杰出的兵书。',
      },
    ],
  },
  
  // 越国 - 姒姓
  {
    factionId: 'yue',
    scenarioId: 'spring_autumn_late',
    rulingFamily: {
      surname: '姒',
      clanName: '越',
      description: '越王勾践，卧薪尝胆',
    },
    members: [
      {
        id: 'sa_late_yue_ruler',
        name: '姒勾践',
        surname: '姒',
        clanName: '越',
        givenName: '勾践',
        gender: 'male',
        birthYear: -50,
        identity: 'ruler',
        title: '越王勾践',
        abilities: {
          intelligence: 85,
          charisma: 80,
          strength: 75,
          diplomacy: 80,
          administration: 85,
          military: 85,
        },
        personality: {
          openness: 70,
          conscientiousness: 95,
          extraversion: 60,
          agreeableness: 50,
          neuroticism: 40,
        },
        school: 'military',
        description: '越王勾践，卧薪尝胆，灭吴称霸',
        historicalNote: '越王勾践，名勾践。被吴王夫差击败后，卧薪尝胆，任用范蠡、文种，最终灭吴称霸，春秋最后一位霸主。',
      },
      {
        id: 'sa_late_yu_advisor',
        name: '范蠡',
        surname: '范',
        givenName: '蠡',
        gender: 'male',
        birthYear: -55,
        identity: 'official',
        title: '上将军',
        abilities: {
          intelligence: 95,
          charisma: 80,
          diplomacy: 85,
          administration: 90,
          military: 80,
        },
        school: 'diplomat',
        description: '范蠡，助勾践灭吴，后经商致富',
        historicalNote: '范蠡，字少伯，春秋末期著名政治家、军事家、经济学家。辅佐勾践灭吴，后辞官经商，被尊为"商圣"。',
      },
    ],
  },
]

// ============================================
// 统一导出
// ============================================

export const ALL_ROYAL_ARCHIVES: FactionRoyalArchive[] = [
  ...SHANG_EARLY_ROYAL_ARCHIVES,
  ...SHANG_MIDDLE_ROYAL_ARCHIVES,
  ...SHANG_LATE_ROYAL_ARCHIVES,
  ...SPRING_AUTUMN_EARLY_ROYAL_ARCHIVES,
  ...SPRING_AUTUMN_MIDDLE_ROYAL_ARCHIVES,
  ...SPRING_AUTUMN_LATE_ROYAL_ARCHIVES,
]

/**
 * 获取势力王族档案
 */
export function getFactionRoyalArchive(
  scenarioId: string,
  factionId: string
): FactionRoyalArchive | undefined {
  return ALL_ROYAL_ARCHIVES.find(
    archive => archive.scenarioId === scenarioId && archive.factionId === factionId
  )
}

/**
 * 获取剧本所有王族档案
 */
export function getScenarioRoyalArchives(scenarioId: string): FactionRoyalArchive[] {
  return ALL_ROYAL_ARCHIVES.filter(archive => archive.scenarioId === scenarioId)
}

/**
 * 获取预设人物
 */
export function getPresetCharacter(
  scenarioId: string,
  factionId: string,
  characterId: string
): PresetCharacter | undefined {
  const archive = getFactionRoyalArchive(scenarioId, factionId)
  if (!archive) return undefined
  return archive.members.find(member => member.id === characterId)
}

// ============================================
// 统一君主数据获取函数（替代 rulerNames.ts）
// ============================================

/**
 * 获取势力君主信息
 * 统一的数据源，所有君主信息都从这里获取
 */
export function getRulerInfo(
  scenarioId: string,
  factionId: string
): { name: string; surname: string; title: string; givenName: string } | null {
  const archive = getFactionRoyalArchive(scenarioId, factionId)
  if (!archive) return null

  const ruler = archive.members.find(m => m.identity === 'ruler')
  if (!ruler) return null

  return {
    name: ruler.name,
    surname: ruler.surname,
    title: ruler.title,
    givenName: ruler.givenName,
  }
}

/**
 * 获取势力君主名（完整姓名）
 */
export function getRulerName(scenarioId: string, factionId: string): string | null {
  const info = getRulerInfo(scenarioId, factionId)
  return info?.name || null
}

/**
 * 获取势力君主姓氏
 */
export function getRulerSurname(scenarioId: string, factionId: string): string | null {
  const info = getRulerInfo(scenarioId, factionId)
  return info?.surname || null
}

/**
 * 获取势力君主称号
 */
export function getRulerTitle(scenarioId: string, factionId: string): string | null {
  const info = getRulerInfo(scenarioId, factionId)
  return info?.title || null
}
